import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import ReactSelect from 'react-select';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import NumberInput from '@/components/inputControls/NumberInput';

import CalendarView from '@/pages/habor-management/components/CalendarView';
import DayEventsModal from '@/pages/habor-management/components/DayEventsModal';
import type {
  CalendarEvent,
  ViewType,
} from '@/pages/habor-management/components/types';

import { Container } from '@/components/container';
import { KeenIcon } from '@/components/keenicons';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';

import {
  PriceUnit,
  ResourceType,
  IClubResourceData,
  InspectionSlotGeneration,
} from '@/types/club-resource.types';

import ClubResourcePreviewModal from '@/pages/club-resource/ClubResourcePreviewModal';

import {
  Inspector,
  useCreateClubResourceMutation,
  useGetAvailableInspectorsQuery,
  useGetClubResourceByIdQuery,
  useTranslateClubResourceContentMutation,
  useUpdateInspectionSlotMutation,
  useUpdateClubResourceMutation,
} from '@/services/ClubResourceService';

import { useInstantCreateInspectionBookingMutation } from '@/services/ClubBookingService';
import { useGetAllMembersQuery } from '@/services/ClubService';
import { useGetEligibleBoatsForInspectionQuery } from '@/services/BoatManagementService';
import type { MemberResponse } from '@/types/club-management.types';
import type { BoatResponse } from '@/types/boat-management.types';

type ApiSlot = {
  id?: string;
  date: string;
  start_time: string;
  end_time: string;
  inspector?: {
    id?: string | null;
    name?: string | null;
    email?: string | null;
  } | null;
};

type EditableSlot = {
  id?: string;
  date: string;
  start_time: string;
  end_time: string;
  inspector_id?: string | null;
  inspector_label?: string | null;
};

export interface AnnualInspectionData {
  id?: string;
  name: string;
  description?: string;
  resource_type?: ResourceType;
  slot_length?: string;
  quantity?: number;
  status: number;
  allow_booking?: boolean;
  define_price?: boolean;
  price?: number | null;
  price_unit?: PriceUnit | null;
  max_bookings_period_days?: number;
  slot_duration_minutes?: number;
  inspector_ids?: string[];
  inspection_slot_generations?: InspectionSlotGeneration[];
  translations?: {
    [languageCode: string]: {
      description?: string;
    };
  };
}

const AnnualInspectionPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage, changeLanguage } = useLanguage();

  const getApiErrorMessage = useCallback((error: unknown): string => {
    const maybeMessage = (error as { data?: { message?: string } })?.data
      ?.message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return maybeMessage;
    }

    const fallbackMessage = (error as { message?: string })?.message;
    if (typeof fallbackMessage === 'string' && fallbackMessage.trim()) {
      return fallbackMessage;
    }

    return '';
  }, []);

  const params = useParams<{ clubId: string; resourceId?: string }>();
  const [searchParams] = useSearchParams();

  const clubId = params.clubId;
  const resourceId = params.resourceId;
  const modeParam = searchParams.get('mode');

  const mode = useMemo<'create' | 'edit' | 'view'>(() => {
    if (modeParam === 'edit' || modeParam === 'view') return modeParam;
    return 'create';
  }, [modeParam]);

  const isReadOnly = mode === 'view';
  const areSlotsReadOnly = mode !== 'create';

  const handleReturn = useCallback(() => {
    // Prefer an explicit return path if provided.
    // This allows callers (e.g. Harbor Details) to control the return destination
    // such as `/harbors/details?tab=...`.
    const state = location.state as {
      returnTo?: string;
      from?: string;
    } | null;

    const returnTo = state?.returnTo;
    const from = state?.from;

    // If returnTo is provided, navigate there exactly as-is.
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    // If from is a path (not a marker string), navigate there.
    if (from && from !== 'club-resources') {
      navigate(from, { replace: true });
      return;
    }

    // Preserve existing behavior for club context.
    if (from === 'club-resources') {
      navigate(`/club-management/${clubId}#club-resources`, {
        state: { activeTab: 'club-resources' },
        replace: true,
      });
      return;
    }

    navigate(-1);
  }, [clubId, location.state, navigate]);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const [localFormData, setLocalFormData] = useState<
    Partial<AnnualInspectionData>
  >({
    name: '',
    description: '',
    status: 1,
    allow_booking: false,
    define_price: false,
    max_bookings_period_days: 30,
    slot_duration_minutes: 60,
    inspector_ids: [],
    inspection_slot_generations: [],
  });

  const { data: membersResponse, isFetching: isFetchingMembers } =
    useGetAllMembersQuery(
      {
        club_id: clubId || undefined,
        page: 1,
        pageSize: 200,
        status: 1,
      },
      {
        skip: !clubId,
      }
    );

  const [instantMemberId, setInstantMemberId] = useState<string>('');
  const [instantBoatId, setInstantBoatId] = useState<string>('');

  const { data: eligibleBoatsResponse, isFetching: isFetchingEligibleBoats } =
    useGetEligibleBoatsForInspectionQuery(
      {
        clubId: clubId || '',
        memberId: instantMemberId,
        page: 1,
        limit: 500,
      },
      {
        skip: !clubId || !instantMemberId,
      }
    );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [inspectionSlots, setInspectionSlots] = useState<EditableSlot[]>([]);
  const [calendarCurrentDate, setCalendarCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<ViewType>('week');
  const [selectedSlot, setSelectedSlot] = useState<EditableSlot | null>(null);
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState<{
    open: boolean;
    date?: Date;
    events?: CalendarEvent[];
  }>({ open: false });

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [translations, setTranslations] = useState<
    Record<string, { description?: string }>
  >({});

  const [createTab, setCreateTab] = useState<'normal' | 'instant'>('normal');

  const [translateClubResourceContent] =
    useTranslateClubResourceContentMutation();
  const [createResource, { isLoading: isCreatingResource }] =
    useCreateClubResourceMutation();
  const [updateResource, { isLoading: isUpdatingResource }] =
    useUpdateClubResourceMutation();

  const [updateInspectionSlot, { isLoading: isUpdatingInspectionSlot }] =
    useUpdateInspectionSlotMutation();

  const [
    instantCreateInspectionBooking,
    { isLoading: isCreatingInstantBooking },
  ] = useInstantCreateInspectionBookingMutation();

  const isSubmitting =
    isCreatingResource ||
    isUpdatingResource ||
    isCreatingInstantBooking ||
    isUpdatingInspectionSlot;

  const { data: resourceData, isLoading: isResourceLoading } =
    useGetClubResourceByIdQuery(resourceId || '', {
      skip: !resourceId || mode === 'create',
    });

  const {
    data: allInspectors,
    isFetching: isFetchingInspectors,
    error: inspectorFetchError,
  } = useGetAvailableInspectorsQuery(
    { club_id: clubId },
    {
      skip: !clubId,
    }
  );

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: string } = {};

    if (!localFormData.name || localFormData.name.trim() === '') {
      newErrors.name = intl.formatMessage({
        id: 'ANNUAL_INSPECTION.VALIDATION.NAME_REQUIRED',
      });
    }

    if (
      !localFormData.inspector_ids ||
      localFormData.inspector_ids.length === 0
    ) {
      newErrors.inspector_ids = 'At least one inspector is required';
    }

    if (
      !localFormData.inspection_slot_generations ||
      localFormData.inspection_slot_generations.length === 0
    ) {
      newErrors.inspection_slot_generations =
        'At least one date/time range is required';
    } else {
      const invalidGeneration = localFormData.inspection_slot_generations.some(
        (g) => {
          if (!g.date || !g.start_time || !g.end_time) return true;
          return false;
        }
      );

      if (invalidGeneration) {
        newErrors.inspection_slot_generations =
          'Please fill date, start time, and end time for all entries';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [localFormData, intl]);

  const validateInstantBooking = useCallback(() => {
    const newErrors: { [key: string]: string } = {};

    if (!localFormData.name || localFormData.name.trim() === '') {
      newErrors.name = intl.formatMessage({
        id: 'ANNUAL_INSPECTION.VALIDATION.NAME_REQUIRED',
      });
    }

    const selectedInspectorIds = localFormData.inspector_ids || [];
    if (selectedInspectorIds.length !== 1) {
      newErrors.inspector_ids = 'Please select exactly one inspector';
    }

    if (!instantMemberId) {
      newErrors.instant_member_id = 'Boater is required';
    }

    if (!instantBoatId) {
      newErrors.instant_boat_id = 'Boat is required';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [
    instantMemberId,
    instantBoatId,
    localFormData.inspector_ids,
    localFormData.name,
    intl,
  ]);

  useEffect(() => {
    if (mode !== 'view') {
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, [mode]);

  useEffect(() => {
    if (resourceData && (mode === 'view' || mode === 'edit')) {
      const dataToUse =
        resourceData as unknown as Partial<AnnualInspectionData> & {
          allow_bookings?: boolean;
          slots?: ApiSlot[];
        };

      const normalizedSlots = (dataToUse.slots || [])
        .filter(
          (s): s is ApiSlot => !!s && !!s.date && !!s.start_time && !!s.end_time
        )
        .map((s) => {
          const inspectorLabel = s.inspector
            ? s.inspector.name || s.inspector.email || null
            : null;

          return {
            id: s.id,
            date: s.date,
            start_time: s.start_time,
            end_time: s.end_time,
            inspector_id: s.inspector?.id ?? null,
            inspector_label: inspectorLabel,
          } satisfies EditableSlot;
        });

      setInspectionSlots(normalizedSlots);

      const derivedInspectionSlotGenerations: InspectionSlotGeneration[] =
        normalizedSlots.length > 0
          ? normalizedSlots.map((s) => ({
              date: s.date,
              start_time: s.start_time,
              end_time: s.end_time,
            }))
          : ((dataToUse.inspection_slot_generations ||
              []) as InspectionSlotGeneration[]);

      const derivedInspectorIds: string[] =
        normalizedSlots.length > 0
          ? Array.from(
              new Set(
                normalizedSlots
                  .map((s) => s.inspector_id)
                  .filter(
                    (id): id is string =>
                      typeof id === 'string' && id.length > 0
                  )
              )
            )
          : dataToUse.inspector_ids || [];

      const formDataToSet: Partial<AnnualInspectionData> = {
        id: (dataToUse as { id?: string }).id,
        name: dataToUse.name,
        description: dataToUse.description,
        slot_length: '1h',
        quantity: 1,
        status: 1,
        allow_booking:
          dataToUse.allow_booking ??
          (dataToUse as { allow_bookings?: boolean }).allow_bookings,
        define_price: dataToUse.define_price,
        price: dataToUse.price,
        price_unit: dataToUse.price_unit,
        resource_type: dataToUse.resource_type,
        max_bookings_period_days: dataToUse.max_bookings_period_days,
        slot_duration_minutes: dataToUse.slot_duration_minutes ?? 60,
        inspector_ids: derivedInspectorIds,
        inspection_slot_generations: derivedInspectionSlotGenerations,
        translations: (dataToUse as AnnualInspectionData).translations,
      };

      setLocalFormData(formDataToSet);

      if ((dataToUse as AnnualInspectionData).translations) {
        setTranslations((dataToUse as AnnualInspectionData).translations || {});
      }
    }

    setErrors({});
  }, [resourceData, mode]);

  useEffect(() => {
    setInstantBoatId('');
  }, [instantMemberId]);

  useEffect(() => {
    if (mode !== 'create') return;
    setErrors({});
  }, [createTab, mode]);

  useEffect(() => {
    if (mode !== 'create') return;
    if (createTab !== 'instant') return;

    const selectedIds = localFormData.inspector_ids || [];
    if (selectedIds.length > 1) {
      setLocalFormData((prev) => ({
        ...prev,
        inspector_ids: [],
      }));
    }
  }, [createTab, localFormData.inspector_ids, mode]);

  useEffect(() => {
    if (!resourceId || mode === 'create') return;
    if (inspectionSlots.length === 0) return;
    const uniqueInspectorIds = Array.from(
      new Set(
        inspectionSlots
          .map((s) => s.inspector_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    );

    setLocalFormData((prev) => ({
      ...prev,
      inspector_ids: uniqueInspectorIds,
    }));
  }, [inspectionSlots, resourceId, mode]);

  useEffect(() => {
    if (inspectorFetchError) {
      const message = getApiErrorMessage(inspectorFetchError);
      enqueueSnackbar(
        message ||
          intl.formatMessage({
            id: 'ANNUAL_INSPECTION.ERROR.FETCH_AVAILABLE_INSPECTORS',
          }),
        { variant: 'error' }
      );
    }
  }, [getApiErrorMessage, inspectorFetchError, enqueueSnackbar, intl]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      let newValue: string | number | boolean =
        type === 'checkbox' ? checked : value;

      if (
        name === 'slot_duration_minutes' ||
        name === 'max_bookings_period_days' ||
        name === 'quantity' ||
        name === 'status'
      ) {
        const parsed = parseInt(String(value), 10);
        newValue = Number.isFinite(parsed) ? parsed : value;
      }

      setLocalFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      }
    },
    [errors]
  );

  const addInspectionGeneration = useCallback(() => {
    setLocalFormData((prev) => {
      const next: Partial<AnnualInspectionData> = { ...prev };
      const list = [...(next.inspection_slot_generations || [])];
      list.push({
        date: '',
        start_time: '09:00',
        end_time: '15:00',
      });
      next.inspection_slot_generations = list;
      return next;
    });
  }, []);

  const removeInspectionGeneration = useCallback((index: number) => {
    setLocalFormData((prev) => {
      const next: Partial<AnnualInspectionData> = { ...prev };
      next.inspection_slot_generations = (
        next.inspection_slot_generations || []
      ).filter((_, i) => i !== index);
      return next;
    });
  }, []);

  const updateInspectionGenerationField = useCallback(
    (
      index: number,
      field: keyof InspectionSlotGeneration,
      value: string | number
    ) => {
      setLocalFormData((prev) => {
        const next: Partial<AnnualInspectionData> = { ...prev };
        const list = [...(next.inspection_slot_generations || [])];
        const current = list[index];
        if (!current) return prev;
        list[index] = {
          ...current,
          [field]: value,
        } as InspectionSlotGeneration;
        next.inspection_slot_generations = list;
        return next;
      });
    },
    []
  );

  const handleTranslationsGenerated = useCallback(
    (newTranslations: Record<string, { description?: string }>) => {
      setTranslations(newTranslations);
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (!clubId) {
      enqueueSnackbar('Club ID is required', { variant: 'error' });
      return;
    }

    if (!validateForm()) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'ANNUAL_INSPECTION.ERROR.VALIDATION' }),
        { variant: 'error' }
      );
      return;
    }

    try {
      const slotDurationMinutes = localFormData.slot_duration_minutes ?? 60;

      let translationsToUse = translations;
      if (
        (!translationsToUse || Object.keys(translationsToUse).length === 0) &&
        localFormData.description
      ) {
        const payload = {
          description: localFormData.description,
          source_language: currentLanguage.code || 'en',
          resource_id: localFormData.id ? String(localFormData.id) : undefined,
        };

        try {
          const response = await translateClubResourceContent(payload).unwrap();
          if (response && response.translations) {
            const normalized = Object.fromEntries(
              Object.entries(
                response.translations as Record<
                  string,
                  { description?: string }
                >
              ).map(([k, v]) => [k.toLowerCase(), v || {}])
            ) as typeof translations;
            translationsToUse = normalized;
          }
        } catch (translateError) {
          const message = getApiErrorMessage(translateError);
          enqueueSnackbar(
            message ||
              intl.formatMessage({
                id: 'ANNUAL_INSPECTION.ERROR.TRANSLATION',
              }),
            { variant: 'warning' }
          );
        }
      }

      const dateWiseSlots = Object.entries(
        inspectionSlots.reduce<Record<string, EditableSlot[]>>((acc, slot) => {
          if (!acc[slot.date]) acc[slot.date] = [];
          acc[slot.date].push(slot);
          return acc;
        }, {})
      ).map(([date, slots]) => ({
        date,
        slots: slots.map((s) => ({
          id: s.id,
          startTime: s.start_time,
          endTime: s.end_time,
          inspector_id: s.inspector_id || undefined,
          inspectorName: s.inspector_label || undefined,
        })),
      }));

      const resourceData: Omit<
        IClubResourceData,
        'id' | 'created_at' | 'updated_at'
      > = {
        name: localFormData.name || '',
        description: localFormData.description || '',
        resource_type: ResourceType.ANNUAL_INSPECTIONS,
        club_id: clubId,
        allow_booking: localFormData.allow_booking || false,
        slot_length: '1h',
        quantity: 1,
        recurrence_pattern: undefined,
        max_bookings_period_days: localFormData.max_bookings_period_days,
        define_price: localFormData.define_price || false,
        price: localFormData.define_price ? localFormData.price : undefined,
        price_unit: localFormData.price_unit || PriceUnit.PER_SLOT,
        slot_duration_minutes: slotDurationMinutes,
        inspector_ids: localFormData.inspector_ids || [],
        inspection_slot_generations: (
          localFormData.inspection_slot_generations || []
        ).map((g) => ({
          date: g.date,
          start_time: g.start_time,
          end_time: g.end_time,
        })),
        date_wise_slots: dateWiseSlots,
        shift_start_date: '',
        shift_end_date: '',
        shift_start_time: '',
        shift_end_time: '',
        seasonal_dates: undefined,
        status: 1,
        translations: translationsToUse,
      };

      if (mode === 'edit') {
        if (!resourceId) {
          enqueueSnackbar('Resource ID is required', { variant: 'error' });
          return;
        }

        await updateResource({ id: resourceId, data: resourceData }).unwrap();
        enqueueSnackbar('Annual inspection updated successfully', {
          variant: 'success',
        });
      } else {
        await createResource(resourceData).unwrap();
        enqueueSnackbar('Annual inspection created successfully', {
          variant: 'success',
        });
      }

      handleReturn();
    } catch (error) {
      const message = getApiErrorMessage(error);
      enqueueSnackbar(
        message || intl.formatMessage({ id: 'ANNUAL_INSPECTION.ERROR.SUBMIT' }),
        {
          variant: 'error',
        }
      );
    }
  }, [
    clubId,
    resourceId,
    localFormData,
    validateForm,
    intl,
    translations,
    currentLanguage.code,
    translateClubResourceContent,
    createResource,
    updateResource,
    mode,
    inspectionSlots,
    handleReturn,
    enqueueSnackbar,
    getApiErrorMessage,
  ]);

  const memberOptions = useMemo(() => {
    const list: MemberResponse[] = Array.isArray(membersResponse?.data)
      ? (membersResponse.data as MemberResponse[])
      : [];

    return list
      .filter((m) => !!m.id && !!m.user?.id)
      .map((m) => ({
        value: m.user?.id || '',
        label: m.user?.name || m.user?.email || m.user?.id,
      }));
  }, [membersResponse?.data]);

  const filteredBoatOptions = useMemo(() => {
    const eligibleList: BoatResponse[] = Array.isArray(
      eligibleBoatsResponse?.data
    )
      ? (eligibleBoatsResponse.data as BoatResponse[])
      : [];

    const filtered = instantMemberId ? eligibleList : [];

    return filtered.map((b) => ({
      value: b.id,
      label: `${b.name}${b.registration_number ? ` (${b.registration_number})` : ''}`,
    }));
  }, [eligibleBoatsResponse?.data, instantMemberId]);

  const handleInstantBookingSubmit = useCallback(async () => {
    if (!clubId) {
      enqueueSnackbar('Club ID is required', { variant: 'error' });
      return;
    }

    if (!validateInstantBooking()) {
      enqueueSnackbar('Please fix the instant booking form errors', {
        variant: 'error',
      });
      return;
    }

    try {
      const selectedInspectorId = localFormData.inspector_ids?.[0] || '';
      const payload = {
        resource: {
          name: localFormData.name || '',
          resource_type: ResourceType.ANNUAL_INSPECTIONS,
          club_id: clubId,
          description: localFormData.description || '',
          language: currentLanguage.code || 'en',
          translations: translations || {},
          status: 1,
          slot_duration_minutes: localFormData.slot_duration_minutes ?? 60,
          inspector_ids: selectedInspectorId ? [selectedInspectorId] : [],
        },
        member_id: instantMemberId,
        boat_id: instantBoatId,
      };

      await instantCreateInspectionBooking(payload).unwrap();

      enqueueSnackbar(
        'Instant annual inspection booking created successfully',
        {
          variant: 'success',
        }
      );
      handleReturn();
    } catch (error) {
      const message = getApiErrorMessage(error);
      enqueueSnackbar(
        message || 'Failed to create instant annual inspection booking',
        {
          variant: 'error',
        }
      );
    }
  }, [
    clubId,
    validateInstantBooking,
    enqueueSnackbar,
    localFormData.name,
    localFormData.description,
    localFormData.inspector_ids,
    localFormData.slot_duration_minutes,
    currentLanguage.code,
    translations,
    instantMemberId,
    instantBoatId,
    instantCreateInspectionBooking,
    handleReturn,
    getApiErrorMessage,
  ]);

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    if (mode === 'create') return [];
    if (inspectionSlots.length === 0) return [];

    return inspectionSlots
      .slice()
      .sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.start_time.localeCompare(b.start_time);
      })
      .map((slot) => {
        const inspectorText = slot.inspector_label || 'Unassigned';
        const title = `${slot.start_time}-${slot.end_time} • ${inspectorText}`;
        return {
          id: `${slot.id || `${slot.date}-${slot.start_time}-${slot.end_time}`}`,
          title,
          start: slot.date,
          end: slot.date,
          status: 'confirmed',
          berthNumber: '-',
          user: {
            name: inspectorText,
            email: inspectorText,
          },
          assetType: 'resource',
          resource: {
            id: localFormData.id,
            name: localFormData.name || 'Annual Inspection',
            description: localFormData.description,
            price: localFormData.price,
            price_unit: localFormData.price_unit,
            quantity: 1,
            status: localFormData.status ?? 1,
            resource_type: ResourceType.ANNUAL_INSPECTIONS,
            club_id: clubId,
          } as unknown as IClubResourceData,
        };
      });
  }, [inspectionSlots, localFormData, clubId, mode]);

  const handleCalendarPrevious = useCallback(() => {
    const newDate = new Date(calendarCurrentDate);
    if (calendarView === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (calendarView === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCalendarCurrentDate(newDate);
  }, [calendarCurrentDate, calendarView]);

  const handleCalendarNext = useCallback(() => {
    const newDate = new Date(calendarCurrentDate);
    if (calendarView === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (calendarView === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCalendarCurrentDate(newDate);
  }, [calendarCurrentDate, calendarView]);

  const handleCalendarToday = useCallback(() => {
    setCalendarCurrentDate(new Date());
  }, []);

  const handleCalendarEventClick = useCallback(
    (event: CalendarEvent) => {
      const slot = inspectionSlots.find((s) => {
        const slotKey = s.id || `${s.date}-${s.start_time}-${s.end_time}`;
        return slotKey === event.id;
      });

      if (!slot) return;
      setSelectedSlot(slot);
      setIsSlotModalOpen(true);
    },
    [inspectionSlots]
  );

  const handleSlotInspectorChange = useCallback(
    (slotKey: string, inspectorId: string | null) => {
      const inspector = (allInspectors || []).find(
        (i: Inspector) => i.id === inspectorId
      );

      setInspectionSlots((prev) =>
        prev.map((s) => {
          const currentKey = s.id || `${s.date}-${s.start_time}-${s.end_time}`;
          if (currentKey !== slotKey) return s;
          return {
            ...s,
            inspector_id: inspectorId,
            inspector_label: inspector
              ? inspector.name || inspector.email
              : null,
          };
        })
      );
    },
    [allInspectors]
  );

  if (!clubId) {
    return (
      <Container>
        <div className="card">
          <div className="card-body">
            <div className="text-red-500">Club ID is required</div>
          </div>
        </div>
      </Container>
    );
  }

  if (isResourceLoading) {
    return (
      <Container>
        <div className="flex justify-center p-8">
          <CircularProgress />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">
          {mode === 'create'
            ? intl.formatMessage({ id: 'ANNUAL_INSPECTION.FORM.CREATE.TITLE' })
            : mode === 'edit'
              ? intl.formatMessage({ id: 'ANNUAL_INSPECTION.FORM.EDIT.TITLE' })
              : intl.formatMessage({ id: 'ANNUAL_INSPECTION.FORM.VIEW.TITLE' })}
        </h1>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <select
              id="annual-inspection-language-select"
              className="select select-bordered pl-8 pr-8 h-10 leading-normal"
              value={currentLanguage.code}
              onChange={(e) => {
                const selectedLang = I18N_LANGUAGES.find(
                  (lang: { code: string; label: string }) =>
                    lang.code === e.target.value
                );
                if (selectedLang) {
                  changeLanguage(selectedLang);
                }
              }}
              aria-label={intl.formatMessage({
                id: 'ANNUAL_INSPECTION.FORM.SELECT_LANGUAGE',
              })}
            >
              {I18N_LANGUAGES.map((lang: { code: string; label: string }) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <img
              src="/media/flags/globe.svg"
              alt="Globe"
              className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
              aria-hidden="true"
            />
          </div>

          <button
            className="btn btn-light"
            type="button"
            onClick={handleReturn}
            disabled={isSubmitting}
          >
            {mode === 'view' ? 'Back' : 'Cancel'}
          </button>

          {!isReadOnly && (
            <button
              className="btn btn-primary"
              type="button"
              onClick={
                mode === 'create' && createTab === 'instant'
                  ? handleInstantBookingSubmit
                  : handleSubmit
              }
              disabled={isSubmitting}
            >
              {mode === 'edit'
                ? 'Save Changes'
                : mode === 'create' && createTab === 'instant'
                  ? 'Create Instant Booking'
                  : 'Create'}
            </button>
          )}
        </div>
      </div>

      {mode === 'create' && (
        <div className="mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 contrast:bg-black p-1">
            <button
              type="button"
              className={clsx(
                'px-3 py-2 text-sm rounded-md',
                createTab === 'normal'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 contrast:text-white/50 hover:bg-gray-100'
              )}
              onClick={() => setCreateTab('normal')}
              disabled={isSubmitting}
            >
              Normal Annual Inspection
            </button>
            <button
              type="button"
              className={clsx(
                'px-3 py-2 text-sm rounded-md',
                createTab === 'instant'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
              onClick={() => setCreateTab('instant')}
              disabled={isSubmitting}
            >
              Instant Booking
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            <div className="col-span-1">
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'ANNUAL_INSPECTION.FORM.NAME' })}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                id="name-input"
                ref={nameInputRef}
                type="text"
                name="name"
                value={localFormData.name || ''}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.name && 'input-error border-red-500'
                )}
                placeholder={intl.formatMessage({
                  id: 'ANNUAL_INSPECTION.FORM.NAME.PLACEHOLDER',
                })}
                disabled={isReadOnly || isSubmitting}
                required
              />
              {errors.name && (
                <span className="text-xs text-red-500 mt-1">{errors.name}</span>
              )}
            </div>

            <div className="col-span-1">
              <label
                htmlFor="annual-inspection-inspectors"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({
                  id: 'ANNUAL_INSPECTION.FORM.INSPECTORS',
                })}{' '}
                <span className="text-red-500">*</span>
              </label>
              <ReactSelect
                inputId="annual-inspection-inspectors"
                isMulti={!(mode === 'create' && createTab === 'instant')}
                isClearable
                isDisabled={isReadOnly || isSubmitting}
                isLoading={isFetchingInspectors}
                classNamePrefix="react-select"
                options={(allInspectors || []).map((ins: Inspector) => ({
                  value: ins.id,
                  label: ins.email || ins.name,
                }))}
                value={(() => {
                  const isInstant =
                    mode === 'create' && createTab === 'instant';
                  const selectedIds = localFormData.inspector_ids || [];

                  if (isInstant) {
                    const id = selectedIds[0];
                    if (!id) return null;
                    const ins = (allInspectors || []).find(
                      (i: Inspector) => i.id === id
                    );
                    return ins
                      ? { value: ins.id, label: ins.email || ins.name }
                      : { value: id, label: id };
                  }

                  return selectedIds
                    .map((id) => {
                      const ins = (allInspectors || []).find(
                        (i: Inspector) => i.id === id
                      );
                      return ins
                        ? { value: ins.id, label: ins.email || ins.name }
                        : { value: id, label: id };
                    })
                    .filter(Boolean);
                })()}
                onChange={(selected) => {
                  const isInstant =
                    mode === 'create' && createTab === 'instant';

                  if (isInstant) {
                    const opt = selected as {
                      value: string;
                      label: string;
                    } | null;
                    setLocalFormData((prev) => ({
                      ...prev,
                      inspector_ids: opt?.value ? [opt.value] : [],
                    }));
                    return;
                  }

                  const selectedArray = (selected || []) as Array<{
                    value: string;
                    label: string;
                  }>;
                  setLocalFormData((prev) => ({
                    ...prev,
                    inspector_ids: selectedArray.map((s) => s.value),
                  }));
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '44px',
                    borderColor: errors.inspector_ids
                      ? '#ef4444'
                      : base.borderColor,
                  }),
                }}
              />
              {errors.inspector_ids && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.inspector_ids}
                </p>
              )}
            </div>

            {(mode !== 'create' || createTab === 'normal') && (
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="block text-sm font-medium text-gray-700">
                    Inspection Slot Generations{' '}
                    <span className="text-red-500">*</span>
                  </span>
                  {!areSlotsReadOnly && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={addInspectionGeneration}
                      disabled={isSubmitting}
                    >
                      Add Date
                    </button>
                  )}
                </div>

                {errors.inspection_slot_generations && (
                  <p className="text-red-500 text-xs mb-2" role="alert">
                    {errors.inspection_slot_generations}
                  </p>
                )}

                <div className="max-h-96 overflow-y-auto pr-1">
                  {areSlotsReadOnly ? (
                    <div className="space-y-4">
                      <DayEventsModal
                        isOpen={dayEventsModal.open}
                        date={dayEventsModal.date}
                        events={dayEventsModal.events}
                        showStatusLabel={false}
                        showAssetTypeLabel={false}
                        onClose={() => setDayEventsModal({ open: false })}
                        onEventClick={(ev) => {
                          handleCalendarEventClick(ev);
                          setDayEventsModal({ open: false });
                        }}
                      />

                      {calendarEvents.length > 0 ? (
                        <CalendarView
                          currentDate={calendarCurrentDate}
                          view={calendarView}
                          setView={setCalendarView}
                          filteredEvents={calendarEvents}
                          showStatusLabel={false}
                          showAssetTypeLabel={false}
                          handlePrevious={handleCalendarPrevious}
                          handleNext={handleCalendarNext}
                          handleToday={handleCalendarToday}
                          handleEventClick={handleCalendarEventClick}
                          onMoreClick={(date, events) =>
                            setDayEventsModal({ open: true, date, events })
                          }
                        />
                      ) : (
                        <div className="text-sm text-gray-600">
                          No slots found
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(localFormData.inspection_slot_generations || []).map(
                        (g, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border border-gray-200 rounded-lg p-3"
                          >
                            <div>
                              <label
                                htmlFor={`slot-date-${idx}`}
                                className="block text-xs text-gray-600 mb-1"
                              >
                                Date
                              </label>
                              <input
                                id={`slot-date-${idx}`}
                                type="date"
                                autoComplete="off"
                                className="input input-bordered w-full h-10"
                                value={g.date}
                                onChange={(e) =>
                                  updateInspectionGenerationField(
                                    idx,
                                    'date',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`slot-start-time-${idx}`}
                                className="block text-xs text-gray-600 mb-1"
                              >
                                Start Time
                              </label>
                              <input
                                id={`slot-start-time-${idx}`}
                                type="time"
                                autoComplete="off"
                                className="input input-bordered w-full h-10"
                                value={g.start_time}
                                onChange={(e) =>
                                  updateInspectionGenerationField(
                                    idx,
                                    'start_time',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`slot-end-time-${idx}`}
                                className="block text-xs text-gray-600 mb-1"
                              >
                                End Time
                              </label>
                              <input
                                id={`slot-end-time-${idx}`}
                                type="time"
                                autoComplete="off"
                                className="input input-bordered w-full h-10"
                                value={g.end_time}
                                onChange={(e) =>
                                  updateInspectionGenerationField(
                                    idx,
                                    'end_time',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                              />
                            </div>
                            <div className="flex justify-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => removeInspectionGeneration(idx)}
                                disabled={isSubmitting}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="col-span-1">
              <label
                htmlFor="annual-inspection-resource-type"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({
                  id: 'ANNUAL_INSPECTION.FORM.RESOURCE_TYPE',
                })}
              </label>
              <input
                id="annual-inspection-resource-type"
                type="text"
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100"
                value={intl.formatMessage({
                  id: 'ANNUAL_INSPECTION.FORM.RESOURCE_TYPE.VALUE',
                })}
                disabled
              />
            </div>

            <div className="col-span-1">
              <label
                htmlFor="slot-duration-minutes-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Slot Duration (minutes)
              </label>
              <select
                id="slot-duration-minutes-select"
                name="slot_duration_minutes"
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.slot_duration_minutes && 'border-red-500'
                )}
                value={localFormData.slot_duration_minutes ?? 60}
                onChange={handleInputChange}
                disabled={isReadOnly || isSubmitting}
                aria-invalid={!!errors.slot_duration_minutes}
                aria-describedby={
                  errors.slot_duration_minutes
                    ? 'slot-duration-minutes-error'
                    : undefined
                }
              >
                <option value={30}>30</option>
                <option value={60}>60</option>
                <option value={90}>90</option>
              </select>
              {errors.slot_duration_minutes && (
                <p
                  id="slot-duration-minutes-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.slot_duration_minutes}
                </p>
              )}
            </div>

            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
              </label>
              <textarea
                id="description-input"
                name="description"
                className={clsx(
                  'textarea textarea-bordered w-full rounded-lg border-gray-300',
                  errors.description && 'border-red-500'
                )}
                value={localFormData.description || ''}
                onChange={handleInputChange}
                disabled={isReadOnly || isSubmitting}
                placeholder={intl.formatMessage({
                  id: 'ANNUAL_INSPECTION.FORM.DESCRIPTION.PLACEHOLDER',
                })}
                rows={4}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
              />
              {errors.description && (
                <p
                  id="description-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {!isReadOnly && localFormData.description && (
              <div className="col-span-1 md:col-span-2 mt-2 bg-blue-50 contrast:bg-blue-950 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-2 annual-inspection-preview-notice">
                <div className="flex items-center">
                  <KeenIcon
                    type="outline"
                    icon="paper-clip"
                    className="w-5 h-5 text-blue-600 mr-2"
                  />
                  <p className="text-blue-700 text-sm annual-inspection-preview-notice-text contrast:text-white">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.PREVIEW.NOTICE',
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPreviewModalOpen(true)}
                  className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 annual-inspection-preview-notice-button"
                  aria-label={intl.formatMessage({
                    id: 'CLUB_RESOURCE.BUTTON.SHOW_PREVIEW',
                  })}
                  disabled={isSubmitting}
                >
                  {intl.formatMessage({
                    id: 'CLUB_RESOURCE.BUTTON.SHOW_PREVIEW',
                  })}
                </button>
              </div>
            )}

            {(mode !== 'create' || createTab === 'normal') && (
              <div className="col-span-1">
                <label
                  htmlFor="allow-booking-checkbox"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({
                    id: 'ANNUAL_INSPECTION.FORM.ALLOW_BOOKING',
                  })}
                </label>
                <div className="flex items-center h-[40px]">
                  <input
                    id="allow-booking-checkbox"
                    name="allow_booking"
                    type="checkbox"
                    className="checkbox cursor-pointer"
                    checked={localFormData.allow_booking || false}
                    onChange={(e) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        allow_booking: e.target.checked,
                      }))
                    }
                    disabled={isReadOnly || isSubmitting}
                    aria-invalid={!!errors.allow_booking}
                    aria-describedby={
                      errors.allow_booking ? 'allow-booking-error' : undefined
                    }
                  />
                  <span className="ml-2 text-sm text-gray-600 cursor-pointer">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.ALLOW_BOOKING.HELP_TEXT',
                    })}
                  </span>
                </div>
                {errors.allow_booking && (
                  <p
                    id="allow-booking-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.allow_booking}
                  </p>
                )}
              </div>
            )}

            {(mode !== 'create' || createTab === 'normal') && (
              <div className="col-span-1">
                <label
                  htmlFor="define-price-checkbox"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({
                    id: 'ANNUAL_INSPECTION.FORM.DEFINE_PRICE',
                  })}
                </label>
                <div className="flex items-center h-[40px]">
                  <input
                    id="define-price-checkbox"
                    name="define_price"
                    type="checkbox"
                    className="checkbox cursor-pointer"
                    checked={localFormData.define_price || false}
                    onChange={(e) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        define_price: e.target.checked,
                      }))
                    }
                    disabled={isReadOnly || isSubmitting}
                    aria-invalid={!!errors.define_price}
                    aria-describedby={
                      errors.define_price ? 'define-price-error' : undefined
                    }
                  />
                  <span className="ml-2 text-sm text-gray-600 cursor-pointer">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.DEFINE_PRICE.HELP_TEXT',
                    })}
                  </span>
                </div>
                {errors.define_price && (
                  <p
                    id="define-price-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.define_price}
                  </p>
                )}
              </div>
            )}

            {(mode !== 'create' || createTab === 'normal') &&
              localFormData.define_price && (
                <>
                  <div className="col-span-1">
                    <label
                      htmlFor="price-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'COMMON.PRICE' })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <NumberInput
                      id="price-input"
                      name="price"
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.price && 'border-red-500'
                      )}
                      value={localFormData.price || ''}
                      onChange={handleInputChange}
                      disabled={isReadOnly || isSubmitting}
                      placeholder={intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.PRICE.PLACEHOLDER',
                      })}
                      min="0"
                      step="0.01"
                      aria-invalid={!!errors.price}
                      aria-describedby={
                        errors.price ? 'price-error' : undefined
                      }
                      autoComplete="transaction-amount"
                    />
                    {errors.price && (
                      <p
                        id="price-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.price}
                      </p>
                    )}
                  </div>

                  <div className="col-span-1">
                    <label
                      htmlFor="price-unit-select"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'ANNUAL_INSPECTION.FORM.PRICE_UNIT',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="price-unit-select"
                      name="price_unit"
                      value={localFormData.price_unit || PriceUnit.PER_SLOT}
                      onChange={handleInputChange}
                      className={clsx(
                        'select select-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.price_unit && 'border-red-500'
                      )}
                      disabled={isReadOnly || isSubmitting}
                      aria-invalid={!!errors.price_unit}
                      aria-describedby={
                        errors.price_unit ? 'price-unit-error' : undefined
                      }
                    >
                      <option value={PriceUnit.PER_SLOT}>
                        {intl.formatMessage({
                          id: 'ANNUAL_INSPECTION.FORM.PRICE_UNIT.PER_SLOT',
                        })}
                      </option>
                      <option value={PriceUnit.PER_HOUR}>
                        {intl.formatMessage({
                          id: 'ANNUAL_INSPECTION.FORM.PRICE_UNIT.PER_HOUR',
                        })}
                      </option>
                    </select>
                    {errors.price_unit && (
                      <p
                        id="price-unit-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.price_unit}
                      </p>
                    )}
                  </div>
                </>
              )}

            {mode === 'create' && createTab === 'instant' && (
              <div className="col-span-1 md:col-span-2">
                <div className="mt-2 border border-gray-200 rounded-xl p-4 bg-gray-50 contrast:bg-black dark:bg-[#363843]">
                  <div className="mb-4">
                    <h2 className="text-base font-semibold text-gray-900">
                      {intl.formatMessage({
                        id: 'ANNUAL_INSPECTION.INSTANT.TITLE',
                      })}
                    </h2>
                    <p className="text-xs text-gray-600 mt-1">
                      {intl.formatMessage({
                        id: 'ANNUAL_INSPECTION.INSTANT.DESCRIPTION',
                      })}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="instant-member-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'ANNUAL_INSPECTION.INSTANT.FIELD.BOATER',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <ReactSelect
                        inputId="instant-member-select"
                        isClearable
                        isDisabled={isSubmitting}
                        isLoading={isFetchingMembers}
                        classNamePrefix="react-select"
                        options={memberOptions}
                        value={
                          instantMemberId
                            ? memberOptions.find(
                                (o) => o.value === instantMemberId
                              ) || null
                            : null
                        }
                        onChange={(selected) => {
                          const opt = selected as {
                            value: string;
                            label: string;
                          } | null;
                          setInstantMemberId(opt?.value || '');
                          if (errors.instant_member_id) {
                            setErrors((prev) => ({
                              ...prev,
                              instant_member_id: '',
                            }));
                          }
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '44px',
                            borderColor: errors.instant_member_id
                              ? '#ef4444'
                              : base.borderColor,
                          }),
                        }}
                      />
                      {errors.instant_member_id && (
                        <p className="text-red-500 text-xs mt-1" role="alert">
                          {errors.instant_member_id}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="instant-boat-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'ANNUAL_INSPECTION.INSTANT.FIELD.BOAT',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <ReactSelect
                        inputId="instant-boat-select"
                        isClearable
                        isDisabled={isSubmitting || !instantMemberId}
                        isLoading={isFetchingEligibleBoats}
                        classNamePrefix="react-select"
                        options={filteredBoatOptions}
                        value={
                          instantBoatId
                            ? filteredBoatOptions.find(
                                (o) => o.value === instantBoatId
                              ) || null
                            : null
                        }
                        onChange={(selected) => {
                          const opt = selected as {
                            value: string;
                            label: string;
                          } | null;
                          setInstantBoatId(opt?.value || '');
                          if (errors.instant_boat_id) {
                            setErrors((prev) => ({
                              ...prev,
                              instant_boat_id: '',
                            }));
                          }
                        }}
                        noOptionsMessage={() =>
                          instantMemberId
                            ? intl.formatMessage({
                                id: 'ANNUAL_INSPECTION.INSTANT.NO_BOATS_FOR_BOATER',
                              })
                            : intl.formatMessage({
                                id: 'ANNUAL_INSPECTION.INSTANT.SELECT_BOATER_FIRST',
                              })
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '44px',
                            borderColor: errors.instant_boat_id
                              ? '#ef4444'
                              : base.borderColor,
                          }),
                        }}
                      />
                      {errors.instant_boat_id && (
                        <p className="text-red-500 text-xs mt-1" role="alert">
                          {errors.instant_boat_id}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-gray-600">
                    {intl.formatMessage({
                      id: 'ANNUAL_INSPECTION.INSTANT.NOTE',
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ClubResourcePreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        formData={localFormData as unknown as Partial<IClubResourceData>}
        onTranslationsGenerated={handleTranslationsGenerated}
        source_language={currentLanguage.code}
      />

      {isSlotModalOpen && selectedSlot && (
        <div
          className="fixed inset-0 text-blue-600 bg-opacity-50 flex items-center justify-center z-[999] p-4"
          onClick={() => setIsSlotModalOpen(false)}
          role="presentation"
        >
          <div
            className="bg-gray-100 rounded-xl shadow-xl max-w-md w-full max-h-[520px] overflow-y-auto custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="annual-inspection-slot-modal-title"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3
                    id="annual-inspection-slot-modal-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Slot Details
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedSlot.date} • {selectedSlot.start_time}-
                    {selectedSlot.end_time}
                  </p>
                </div>
                <button
                  onClick={() => setIsSlotModalOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close modal"
                  type="button"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="border rounded-lg p-3 bg-gray-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-medium text-gray-900">
                        {selectedSlot.date}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Time</div>
                      <div className="font-medium text-gray-900">
                        {selectedSlot.start_time} - {selectedSlot.end_time}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500">Inspector</div>
                      <div className="font-medium text-gray-900">
                        {selectedSlot.inspector_label || 'Unassigned'}
                      </div>
                    </div>
                  </div>
                </div>

                {mode === 'edit' && (
                  <div>
                    <label
                      htmlFor="annual-inspection-slot-inspector"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Change Inspector
                    </label>
                    <select
                      id="annual-inspection-slot-inspector"
                      className="select select-bordered w-full"
                      value={selectedSlot.inspector_id || ''}
                      onChange={(e) => {
                        const key =
                          selectedSlot.id ||
                          `${selectedSlot.date}-${selectedSlot.start_time}-${selectedSlot.end_time}`;
                        const val = e.target.value || '';
                        handleSlotInspectorChange(key, val ? val : null);
                        const inspector = (allInspectors || []).find(
                          (i: Inspector) => i.id === val
                        );
                        setSelectedSlot((prev) =>
                          prev
                            ? {
                                ...prev,
                                inspector_id: val ? val : null,
                                inspector_label: inspector
                                  ? inspector.email || inspector.name
                                  : null,
                              }
                            : prev
                        );
                      }}
                      disabled={isSubmitting}
                    >
                      <option value="">Unassigned</option>
                      {(allInspectors || []).map((ins: Inspector) => (
                        <option key={ins.id} value={ins.id}>
                          {ins.email || ins.name}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm mt-3 w-full"
                      disabled={
                        isSubmitting ||
                        !selectedSlot.id ||
                        !selectedSlot.inspector_id ||
                        !selectedSlot.date ||
                        !selectedSlot.start_time ||
                        !selectedSlot.end_time
                      }
                      onClick={async () => {
                        if (!selectedSlot.id) return;
                        if (!selectedSlot.inspector_id) {
                          enqueueSnackbar('Inspector is required', {
                            variant: 'error',
                          });
                          return;
                        }

                        try {
                          await updateInspectionSlot({
                            id: String(selectedSlot.id),
                            data: {
                              date: selectedSlot.date,
                              start_time: selectedSlot.start_time,
                              end_time: selectedSlot.end_time,
                              inspector_id: String(selectedSlot.inspector_id),
                            },
                          }).unwrap();

                          enqueueSnackbar('Inspector updated successfully', {
                            variant: 'success',
                          });
                          setIsSlotModalOpen(false);
                        } catch (error) {
                          const message = getApiErrorMessage(error);
                          enqueueSnackbar(
                            message || 'Failed to update inspector',
                            {
                              variant: 'error',
                            }
                          );
                        }
                      }}
                    >
                      Update Inspector
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsSlotModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default AnnualInspectionPage;
